import { type KeyboardEvent, Suspense, useEffect, useState } from "react";

import {
  AlertingIcon,
  Box,
  Column,
  IconButton,
  Tooltip,
} from "@hightouch/htui";

import { SIDEBAR_TOGGLE_PROPS } from "src/components/layout/nav/mini-nav/mini-nav-context";
import { SettingsView } from "src/components/layout/settings-view";
import { OVERLAY_SELECTOR } from "src/pages/projects/board-shortcuts";
import {
  unreadLabel,
  useInboxUnreadCount,
} from "src/pages/projects/inbox/inbox-queries";
import { InboxSkeleton } from "src/pages/projects/inbox/inbox-skeleton";
import { lazyNamed } from "src/utils/lazy-named";
import { whenIdle } from "src/utils/when-idle";

// One import shared by the warm-up and the view, which a failure clears so the next try fetches again.
let modalContentImport: Promise<
  typeof import("src/pages/projects/inbox/inbox-modal-content")
> | null = null;
const importInboxModalContent = () => {
  modalContentImport ??= import(
    "src/pages/projects/inbox/inbox-modal-content"
  ).catch((error: unknown) => {
    modalContentImport = null;
    throw error;
  });
  return modalContentImport;
};
const importPrefetch = () => import("src/pages/projects/prefetch");

// After boot's own requests, so the warm-up never competes with the page the viewer opened.
const WARM_DELAY_MS = 5_000;

/** Loads the inbox's code and data ahead of an open, so it shows at once. Quiet on failure: opening loads them again. */
function warmInbox() {
  importInboxModalContent().catch(() => {});
  importPrefetch()
    .then(({ prefetchInbox }) => prefetchInbox())
    .catch(() => {});
}
const InboxModalContent = lazyNamed(
  importInboxModalContent,
  "InboxModalContent",
);

// Escape in these belongs to them: a field clears or leaves its text, and a menu or dialog over the inbox closes itself.
const ESCAPE_HANDLED_WITHIN = `input, textarea, select, [contenteditable="true"], ${OVERLAY_SELECTOR}`;

/** The inbox behind a button in the nav's header, since neither nav has an Inbox entry: the inbox opens full screen over the page, as settings do. */
export function InboxButton() {
  const [isOpen, setIsOpen] = useState(false);
  const unreadCount = useInboxUnreadCount({ isEnabled: true });
  // The view hands focus back to the button as it closes; that focus is the view's, so it opens no tooltip.
  const [isFocusReturned, setIsFocusReturned] = useState(false);

  useEffect(() => {
    let cancelIdle: (() => void) | undefined;
    const timer = setTimeout(() => {
      cancelIdle = whenIdle(warmInbox);
    }, WARM_DELAY_MS);
    return () => {
      clearTimeout(timer);
      cancelIdle?.();
    };
  }, []);

  // The pointer's next move clears it, before it can reach the button, so a hover after the inbox shows the tooltip.
  useEffect(() => {
    if (!isFocusReturned) return;
    const clear = () => setIsFocusReturned(false);
    document.addEventListener("pointermove", clear, { once: true });
    return () => document.removeEventListener("pointermove", clear);
  }, [isFocusReturned]);

  return (
    <>
      {/* Flex, so the button sits in the header's row rather than on a line of text. */}
      <Box position="relative" display="flex">
        <Tooltip
          message="Open inbox"
          isDisabled={isOpen || isFocusReturned}
          // The same button stays mounted, so the view can hand focus back to it.
          keepMountedWhileDisabled
        >
          <IconButton
            aria-label={unreadLabel("Open inbox", unreadCount) ?? "Open inbox"}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            icon={AlertingIcon}
            {...SIDEBAR_TOGGLE_PROPS}
            // Data gone stale since the warm-up refetches before the click lands.
            onPointerEnter={warmInbox}
            onFocus={warmInbox}
            onClick={() => setIsOpen(true)}
            onBlur={() => setIsFocusReturned(false)}
          />
        </Tooltip>
        {unreadCount.count > 0 && (
          // A dot, not the count, so it never outweighs the bell; the button's label carries the number.
          // Its ring is the sidebar's own background, so the dot sits clear of the bell's outline.
          <Box
            aria-hidden
            data-inbox-unread
            position="absolute"
            top={0.5}
            right={0.5}
            p={0.5}
            borderRadius="full"
            bg="base.background"
            pointerEvents="none"
          >
            <Box boxSize={2} borderRadius="full" bg="primary.base" />
          </Box>
        )}
      </Box>
      <InboxModal
        isOpen={isOpen}
        onClose={() => {
          setIsFocusReturned(true);
          setIsOpen(false);
        }}
      />
    </>
  );
}

type InboxModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

/** The inbox full screen over the page. It leaves the page's URL alone: the open notification lives in the view's state. */
export function InboxModal({ isOpen, onClose }: InboxModalProps) {
  // Mounted through its exit animation, and fresh on each open, so it reads the page's open overlays as it opens.
  const [isMounted, setIsMounted] = useState(isOpen);
  // The inbox renders once the view has opened: its list is too heavy to render while the entrance plays.
  const [hasOpened, setHasOpened] = useState(false);
  if (isOpen && !isMounted) setIsMounted(true);

  // An open that no hover or focus warmed, as from a menu, loads here.
  useEffect(() => {
    if (isOpen) warmInbox();
  }, [isOpen]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key !== "Escape" ||
      event.defaultPrevented ||
      event.nativeEvent.isComposing
    ) {
      return;
    }
    const target = event.target;
    const owner =
      target instanceof Element ? target.closest(ESCAPE_HANDLED_WITHIN) : null;
    // The view is a dialog too, and Escape on it, or on nothing that claims it, closes the inbox.
    if (owner && owner !== event.currentTarget) {
      return;
    }
    event.preventDefault();
    onClose();
  };

  if (!isMounted) return null;
  return (
    <SettingsView
      isOpen={isOpen}
      animateOnMount
      onOpenComplete={() => setHasOpened(true)}
      onKeyDown={handleKeyDown}
      onScrimClick={onClose}
      onCloseComplete={() => {
        setIsMounted(false);
        setHasOpened(false);
      }}
      label="Inbox"
    >
      <Column flex={1} minHeight={0}>
        {/* The skeleton holds the inbox's layout while the entrance plays and its code loads. */}
        {hasOpened ? (
          <Suspense fallback={<InboxSkeleton onClose={onClose} />}>
            <InboxModalContent onClose={onClose} />
          </Suspense>
        ) : (
          <InboxSkeleton onClose={onClose} />
        )}
      </Column>
    </SettingsView>
  );
}
